import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { StatusChip, BureauChip, TimerBadge, OutcomeChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send } from "lucide-react";
import { toast } from "sonner";

export default function Disputes() {
  const [disputes, setDisputes] = useState([]);
  const [status, setStatus] = useState("all");
  const [sending, setSending] = useState(false);

  const load = () => {
    const params = status !== "all" ? { status } : {};
    api.get("/disputes", { params }).then((r) => setDisputes(r.data)).catch(() => {});
  };
  useEffect(load, [status]);

  const batchSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/disputes/send-batch");
      toast.success(`${r.data.sent} dispute letter${r.data.sent === 1 ? "" : "s"} submitted to certified mail${r.data.sent ? "" : " — nothing drafted"}`);
      if (r.data.errors?.length) toast.error(r.data.errors.join("; "));
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div className="space-y-5" data-testid="disputes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Disputes & Certified Mail</h1>
          <p className="text-sm text-slate-500">Letters are mailed to bureaus via Click2Mail certified mail with tracking. Email is never the submission channel.</p>
        </div>
        <div className="flex gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="dispute-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="drafted">Drafted</SelectItem>
              <SelectItem value="mailed">Mailed</SelectItem>
              <SelectItem value="in_review">In Bureau Review</SelectItem>
              <SelectItem value="responded">Responded</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={batchSend} disabled={sending} data-testid="batch-send-disputes-btn" className="bg-[#0B2545] hover:bg-[#134074] text-white">
            <Send className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Mail all drafted"}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              {["Client", "Letter", "Bureau", "Status", "30-Day Clock", "USPS Tracking", "Mode", "Outcome"].map((h) => (
                <th key={h} className="text-left py-2.5 px-3.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {disputes.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                <td className="py-2.5 px-3.5 font-medium text-slate-800">{d.client_name}</td>
                <td className="py-2.5 px-3.5">
                  <Link to={`/admin/disputes/${d.id}`} className="text-blue-700 hover:underline font-medium" data-testid={`dispute-link-${d.id.slice(0, 8)}`}>{d.template_name}</Link>
                  {d.auto_drafted && <span className="ml-2 text-[10px] font-semibold uppercase text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">Auto-drafted</span>}
                  <div className="text-xs text-slate-400">{fmtDate(d.created_at)}</div>
                </td>
                <td className="py-2.5 px-3.5"><BureauChip bureau={d.bureau} /></td>
                <td className="py-2.5 px-3.5"><StatusChip status={d.status} /></td>
                <td className="py-2.5 px-3.5"><TimerBadge daysLeft={d.days_left} status={d.status} /></td>
                <td className="py-2.5 px-3.5 text-xs font-mono text-slate-500">{d.tracking_number || "—"}</td>
                <td className="py-2.5 px-3.5">
                  <span className={`text-xs font-semibold ${d.mock ? "text-amber-700" : "text-emerald-700"}`}>{d.mock ? "MOCK" : "LIVE"}</span>
                </td>
                <td className="py-2.5 px-3.5"><OutcomeChip outcome={d.outcome} /></td>
              </tr>
            ))}
            {disputes.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">No disputes found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
